import { memo, useCallback, useEffect, useState } from 'react';

import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { FileViewerModel } from 'models';

import { useValidatedParam } from 'core/hooks';
import { Button, Icon } from 'core/ui';

import { FileUrlService } from 'features/exam/services/file-url-service';
import { FileViewerPage } from 'features/file';

import { ShareTopHeader } from './ShareTopHeader';

export const ShareFileViewerPage = memo(() => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const { fileId, shareLinkId } = useParams<{ fileId: string; shareLinkId: string }>();
  const examIdFromUrl = useValidatedParam('examId', 'number', false);

  const [fileViewerData, setFileViewerData] = useState<FileViewerModel | null>(null);
  const [currentViewType, setCurrentViewType] = useState<'primary' | 'secondary' | 'tertiary'>('primary');
  const [isLoading, setIsLoading] = useState(false);

  // Handle navigation back to exams list
  const handleClose = useCallback(() => {
    if (shareLinkId) {
      navigate(`/share/${shareLinkId}/exams`);
    } else {
      // Fallback - go back in history
      navigate(-1);
    }
  }, [navigate, shareLinkId]);

  // Create close button action
  const closeAction = (
    <Button onClick={handleClose} title="Close and return to exams list">
      <Icon fixedWidth={false} icon={faXmark} />
    </Button>
  );

  // Load enhanced file data when fileId changes
  useEffect(() => {
    if (!fileId) return;

    const loadFileData = async () => {
      setIsLoading(true);
      try {
        const data = await FileUrlService.getFileForViewer(parseInt(fileId, 10), true);
        setFileViewerData(data);
        // Reset to primary view when loading new file
        setCurrentViewType('primary');
      } catch (error) {
        console.error('Error loading file viewer data:', error);
        setFileViewerData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadFileData();
  }, [fileId]);

  // Handle backup viewer switch
  const handleSwitchViewer = useCallback(() => {
    if (!fileViewerData?.hasBackupUrls) return;

    // Cycle through available viewers
    switch (currentViewType) {
      case 'primary':
        if (fileViewerData.secondaryViewUrl) {
          setCurrentViewType('secondary');
        } else if (fileViewerData.tertiaryViewUrl) {
          setCurrentViewType('tertiary');
        }
        break;
      case 'secondary':
        if (fileViewerData.tertiaryViewUrl) {
          setCurrentViewType('tertiary');
        } else {
          setCurrentViewType('primary');
        }
        break;
      case 'tertiary':
        setCurrentViewType('primary');
        break;
    }
  }, [fileViewerData, currentViewType]);

  // Get the current view URL
  const currentViewUrl = FileUrlService.getViewUrlFromFileData(fileViewerData, currentViewType);

  // If we have enhanced file data with backup viewers, show our enhanced viewer
  if (fileViewerData && fileViewerData.hasBackupUrls && currentViewUrl) {
    return (
      <StyledEnhancedViewer>
        {/* Share Top Header with backup viewer functionality */}
        <ShareTopHeader showBackupViewer={true} currentViewerType={currentViewType} onSwitchViewer={handleSwitchViewer} actions={closeAction} />

        {/* Main Viewer */}
        <StyledViewerContainer>
          <iframe
            src={currentViewUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            allow="fullscreen; clipboard-read; clipboard-write"
            title={`File Viewer - ${currentViewType}`}
          />
        </StyledViewerContainer>
      </StyledEnhancedViewer>
    );
  }

  // Fall back to regular FileViewerPage while loading, but still show header
  // Also for files without backup viewers, show the normal FileViewerPage with ShareTopHeader
  return (
    <StyledEnhancedViewer>
      <ShareTopHeader actions={closeAction} />
      <StyledViewerContainer>
        <FileViewerPage />
      </StyledViewerContainer>
    </StyledEnhancedViewer>
  );
});

ShareFileViewerPage.displayName = 'ShareFileViewerPage';

const StyledEnhancedViewer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const StyledViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .file-viewer-page {
    height: 100%;
    overflow: hidden !important;
    width: 100% !important;
    max-height: 100vh !important; //todo: improve auto calculation
    min-height: calc(100vh - 40px);
  }
`;
